import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { type ReactElement, useRef, useState } from 'react'
import type { RefCallBack } from 'react-hook-form'
import type { InputInFormProps } from '~/components/Form/Input/input'
import {
  errorMessage,
  inputError,
  inputField,
  inputLabel,
  inputWrapper,
  textarea,
  textareaAutoResize,
  textareaInfo,
  textareaInfoHeader,
  textareaInfoTrigger
} from './TextInput.css'
import { useTextareaAutoResize } from './useTextareaAutoResize'

type TextareaInputProps = React.HTMLProps<HTMLTextAreaElement> &
  InputInFormProps & {
    label?: string
    name?: string
    contextInfo?: string
    standalone?: boolean
    autoResize?: boolean
  }

export const TextareaInput = ({
  control,
  register,
  label,
  name,
  error,
  onChange,
  watch,
  setValue,
  className,
  contextInfo,
  standalone = false,
  autoResize = false,
  ...rest
}: TextareaInputProps): ReactElement => {
  const [showInfo, setShowInfo] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const resize = useTextareaAutoResize({
    ref: textareaRef,
    enabled: autoResize,
    name: standalone ? undefined : name,
    watch: standalone ? undefined : watch
  })

  let registerRef: RefCallBack | undefined
  let attributes: React.TextareaHTMLAttributes<HTMLTextAreaElement>
  if (
    !standalone &&
    register !== undefined &&
    control !== undefined &&
    name !== undefined
  ) {
    const { onChange: onChangeRegister, ref, ...registerRest } = register(name)
    registerRef = ref
    attributes = {
      ...rest,
      ...registerRest,
      onChange: (e) => {
        onChange?.(e)
        onChangeRegister(e)
      }
    }
  } else if (!standalone) {
    throw new Error('TextareaInput must be used inside a Form component')
  } else {
    attributes = onChange !== undefined ? { ...rest, onChange } : { ...rest }
  }

  return (
    <div className={inputWrapper}>
      {label && (
        <label htmlFor={name} className={inputLabel}>
          {label}
        </label>
      )}
      <div className={inputField}>
        <textarea
          className={clsx(
            textarea,
            autoResize && textareaAutoResize,
            error !== undefined && inputError,
            className,
            attributes.className
          )}
          aria-invalid={error !== undefined ? 'true' : 'false'}
          aria-label={label}
          id={attributes.id}
          {...attributes}
          ref={(element) => {
            textareaRef.current = element
            registerRef?.(element)
          }}
          onInput={(e) => {
            attributes.onInput?.(e)
            resize()
          }}
        />
        {contextInfo && (
          <div onMouseLeave={() => setShowInfo(false)}>
            <AnimatePresence>
              <span
                className={textareaInfoTrigger}
                onMouseEnter={() => setShowInfo(true)}
                key="trigger"
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
              {showInfo && (
                <motion.div
                  initial={{ opacity: 0, x: 10, y: 10 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, x: 10, y: 10 }}
                  className={textareaInfo}
                  key="info"
                >
                  <div className={textareaInfoHeader}>
                    <FontAwesomeIcon icon={faInfoCircle} /> Informations
                  </div>
                  {contextInfo}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
      {error !== undefined && (
        <div className={errorMessage} role="alert">
          {error.message}
        </div>
      )}
    </div>
  )
}
